import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Smartphone } from "lucide-react";

/**
 * PWA install prompt.
 * - Chrome/Edge/Android: catches `beforeinstallprompt`, shows a native "Install app" button
 * - iOS Safari: shows a one-time hint ("Tap Share → Add to Home Screen")
 * - Dismissed state remembered for 30 days in localStorage
 */
const DISMISS_KEY = "agriagent-install-dismissed";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Respect prior dismissal (30-day TTL)
    const raw = localStorage.getItem(DISMISS_KEY);
    if (raw && Date.now() - parseInt(raw, 10) < DISMISS_TTL_MS) {
      setDismissed(true);
      return;
    }

    // Standalone check (already installed)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Chrome / Edge / Android
    const onBip = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari — no beforeinstallprompt, show hint
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
    if (isIos && isSafari) {
      // Delay hint 4s so page has time to render
      const t = setTimeout(() => setShowIosHint(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBip); };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIosHint(false);
  };

  if (dismissed) return null;
  if (!deferredPrompt && !showIosHint) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-[80px] sm:bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-sm animate-fade-up"
    >
      <div className="bg-icrisat-green text-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3">
        <Smartphone className="h-5 w-5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {deferredPrompt ? "Install AgriAgent" : "Add to Home Screen"}
          </p>
          <p className="text-xs text-white/80 truncate">
            {deferredPrompt
              ? "Works offline · fast, one-tap access"
              : "Tap Share → Add to Home Screen"}
          </p>
        </div>
        {deferredPrompt && (
          <Button
            size="sm"
            onClick={install}
            data-testid="pwa-install-btn"
            className="bg-white text-icrisat-green hover:bg-white/90 shrink-0"
          >
            <Download className="h-4 w-4 mr-1" /> Install
          </Button>
        )}
        <button
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          className="text-white/80 hover:text-white p-1 shrink-0"
          aria-label="Dismiss install prompt"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
